import { ExternalLinkIcon, FileTextIcon, GlobeIcon, MessageSquareIcon, QuoteIcon, ScaleIcon, TriangleAlertIcon, type LucideIcon } from "lucide-react";
import type { EvidenceCitation, EvidenceKind, FactEvidence } from "../../../../shared/api.ts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { cn } from "@/lib/utils.ts";

const KIND_ICONS: Record<EvidenceKind, LucideIcon> = {
  page: GlobeIcon,
  pdf: FileTextIcon,
  file: FileTextIcon,
  upload: FileTextIcon,
  message: MessageSquareIcon,
  bti: ScaleIcon,
};

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** Where a quote comes from, as a link to the page, the attached file, or the ruling. A user message has no link. */
export function CitationSource({ citation }: { citation: EvidenceCitation }) {
  const { t } = useI18n();
  const Icon = KIND_ICONS[citation.kind];
  const label = citation.kind === "message" ? t.panel.citationMessage : citation.title || citation.source;
  const host = citation.kind === "page" || citation.kind === "pdf" || citation.kind === "file" ? hostOf(citation.source) : null;
  const content = (
    <>
      <Icon className="size-3 shrink-0" aria-hidden />
      <span className="min-w-0 truncate">{label}</span>
      {host && host !== label ? <span className="shrink-0 font-normal text-muted-foreground">{host}</span> : null}
    </>
  );
  if (!citation.url) {
    return <span className="inline-flex max-w-full items-center gap-1 text-[11.5px] text-muted-foreground">{content}</span>;
  }
  return (
    <a
      href={citation.url}
      target="_blank"
      rel="noopener noreferrer"
      title={citation.kind === "upload" ? citation.title : citation.source}
      className="inline-flex max-w-full items-center gap-1 rounded-sm text-[11.5px] font-medium text-primary hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/35 focus-visible:outline-none"
    >
      {content}
      <ExternalLinkIcon className="size-3 shrink-0" aria-hidden />
      <span className="sr-only">{t.panel.citationOpen}</span>
    </a>
  );
}

/**
 * A verified quote in the source's own words, with its source under it. `compact` keeps a long quote to three lines,
 * the whole of it in the tooltip, for a list that shows one under each entry; `source` false leaves out the source
 * line where the entry above already links to it, as a ruling quoted from itself does.
 */
export function CitationQuote({ citation, compact, source = true, className }: { citation: EvidenceCitation; compact?: boolean; source?: boolean; className?: string }) {
  const { t } = useI18n();
  return (
    <figure className={cn("flex min-w-0 flex-col gap-1", className)}>
      <blockquote
        title={compact ? citation.quote : undefined}
        className={cn("border-l-2 border-primary/30 pl-2.5 leading-relaxed break-words text-foreground/90", compact ? "line-clamp-3 text-[12px]" : "text-[12.5px]")}
      >
        {t.panel.quoted(citation.quote)}
      </blockquote>
      {source ? (
        <figcaption className="min-w-0 pl-2.5">
          <CitationSource citation={citation} />
        </figcaption>
      ) : null}
    </figure>
  );
}

/**
 * The sources behind one fact: «2 джерела», or the count marked unconfirmed in amber. The popover lists the verified
 * quotes with links to their sources.
 */
export function FactSources({ entry, label }: { entry: FactEvidence; label: string }) {
  const { t } = useI18n();
  const weak = entry.unconfirmed;
  const count = entry.sources === 0 ? t.panel.factNoSource : t.panel.factSources(entry.sources);
  const Icon = weak ? TriangleAlertIcon : QuoteIcon;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${t.panel.factSourcesTitle(label)}: ${count}${weak ? `, ${t.panel.factUnconfirmed}` : ""}`}
          className={cn(
            "inline-flex items-center gap-1 rounded-sm text-[11.5px] leading-5 font-medium hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/35 focus-visible:outline-none",
            weak ? "text-warning" : "text-muted-foreground hover:text-foreground",
          )}
        >
          <Icon className="size-3 shrink-0" aria-hidden />
          {count}
          {weak ? <span aria-hidden>· {t.panel.factUnconfirmed}</span> : null}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[22rem] max-w-[calc(100vw-2rem)]">
        <p className="text-[12.5px] font-semibold">{t.panel.factSourcesTitle(label)}</p>
        {weak ? (
          <p className="mt-1 text-[12px] leading-relaxed text-warning">{entry.sources === 0 ? t.panel.factNoQuoteHint : t.panel.factUnconfirmedHint}</p>
        ) : null}
        {entry.citations.length > 0 ? (
          <ul className="mt-2.5 flex max-h-72 flex-col gap-3 overflow-y-auto pr-1">
            {entry.citations.map((citation, index) => (
              <li key={`${citation.source}:${index}`}>
                <CitationQuote citation={citation} />
              </li>
            ))}
          </ul>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
